import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { useCompatibility } from "./useCompatibility";
import { SynastryReport } from "./types";
import {
  ArrowLeft,
  MessageCircle,
  Heart,
  Sparkles,
  Clock,
  ChevronRight,
} from "lucide-react";

interface LocationState {
  otherUserName?: string;
}

const getScoreColor = (score: number): string => {
  if (score >= 80) return "text-emerald-500";
  if (score >= 60) return "text-amber-500";
  return "text-rose-400";
};

const getScoreBg = (score: number): string => {
  if (score >= 80) return "bg-emerald-50";
  if (score >= 60) return "bg-amber-50";
  return "bg-rose-50";
};

export const SynastryReportScreen: React.FC = () => {
  const { requestId } = useParams<{ requestId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const otherUserName = (location.state as LocationState | null)?.otherUserName;
  const { fetchSynastryReport } = useCompatibility();

  const [report, setReport] = useState<SynastryReport | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    if (!requestId) return;
    setLoading(true);
    fetchSynastryReport(requestId)
      .then((res) => setReport(res.data))
      .catch(() => toast.error("Failed to load your compatibility report."))
      .finally(() => setLoading(false));
  }, [requestId, fetchSynastryReport]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-rose-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-500 text-sm">Loading your report...</p>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 text-center max-w-md shadow-sm">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Heart className="w-8 h-8 text-gray-400" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Report Not Available
          </h2>
          <p className="text-gray-500 text-sm mb-6">
            We couldn't find a compatibility report for this connection.
          </p>
          <button
            type="button"
            className="px-6 py-2.5 bg-rose-400 text-white font-medium rounded-xl hover:bg-rose-500 transition-colors"
            onClick={() => navigate("/history")}
          >
            Back to History
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate("/history")}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">
              {otherUserName || "Connection"}
            </h1>
            <p className="text-xs text-gray-400">Compatibility Report</p>
          </div>
        </div>

        {/* Overall Score */}
        <div className="bg-white rounded-2xl p-6 mb-6 shadow-sm text-center">
          <div className="flex items-center justify-center gap-2 mb-1">
            <Heart className="w-4 h-4 text-rose-400" />
            <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">
              Connection Score
            </span>
          </div>
          <div
            className={`text-5xl font-bold ${getScoreColor(report.overallScore)}`}
          >
            {report.overallScore}%
          </div>
          <div className="w-32 h-1.5 bg-gray-100 rounded-full mx-auto mt-3 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-1000 ${
                report.overallScore >= 80
                  ? "bg-emerald-400"
                  : report.overallScore >= 60
                    ? "bg-amber-400"
                    : "bg-rose-400"
              }`}
              style={{ width: `${report.overallScore}%` }}
            />
          </div>
          {report.bestTimeToTalk && (
            <p className="text-xs text-gray-400 mt-3 flex items-center justify-center gap-1">
              <Clock className="w-3 h-3" />
              Best time:{" "}
              <span className="text-gray-600">{report.bestTimeToTalk}</span>
            </p>
          )}
        </div>

        {/* Themes */}
        <div className="space-y-3">
          {report.themes.map((theme) => (
            <div key={theme.id} className="bg-white rounded-2xl p-5 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-900">
                  {theme.label}
                </h3>
                <span
                  className={`text-sm font-bold ${getScoreColor(theme.score)}`}
                >
                  {theme.score}%
                </span>
              </div>

              <p className="text-sm text-gray-500 mb-3">{theme.summary}</p>

              {theme.strengths.length > 0 && (
                <div className="mb-2">
                  <p className="text-xs font-medium text-emerald-600 mb-1">
                    ✓ Strengths
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {theme.strengths.map((s, i) => (
                      <span
                        key={i}
                        className="text-xs px-2.5 py-1 bg-emerald-50 text-emerald-700 rounded-full"
                      >
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {theme.frictions.length > 0 && (
                <div className="mb-2">
                  <p className="text-xs font-medium text-amber-600 mb-1">
                    ⚠️ Watch Out
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {theme.frictions.map((s, i) => (
                      <span
                        key={i}
                        className="text-xs px-2.5 py-1 bg-amber-50 text-amber-700 rounded-full"
                      >
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {theme.guidance.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-rose-400 mb-1">
                    💡 Guidance
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {theme.guidance.map((s, i) => (
                      <span
                        key={i}
                        className="text-xs px-2.5 py-1 bg-rose-50 text-rose-600 rounded-full"
                      >
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Actions */}
        <div className="flex gap-3 mt-6">
          <button
            type="button"
            className="flex-1 px-4 py-2.5 bg-white text-gray-600 font-medium rounded-xl border border-gray-200 hover:bg-gray-50 transition-colors text-sm"
            onClick={() => navigate("/history")}
          >
            History
          </button>
          <button
            type="button"
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-rose-400 text-white font-medium rounded-xl hover:bg-rose-500 transition-colors text-sm"
            onClick={() =>
              requestId &&
              navigate(`/compatibility-chat/${requestId}`, {
                state: { otherUserName },
              })
            }
          >
            <MessageCircle className="w-4 h-4" />
            Message
          </button>
        </div>
      </div>
    </div>
  );
};

export default SynastryReportScreen;
